import React, { useEffect, useMemo, useRef, useState } from "react";
import Icons from "../icons";
import ChatMarkdown from "./ChatMarkdown";
import { useConfirm } from "./ConfirmModal";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* The script editor's AI assistant (artifact_ai_service).
 *
 * A chat beside the Inspector. Nothing is sent until the user starts a
 * session: the start screen says what it reads (the work packet from GitHub,
 * the latest runs, the script as the editor holds it) and that each question
 * uses tokens. Starting reads that context (no AI call) and the assistant
 * introduces itself, with starters (Suggest changes only when the latest run
 * failed). Every answer ends with the next steps as buttons, plus Other… to
 * type an answer; a proposed script comes as a card (View changes, Apply to
 * editor). Nothing is saved from here: Save in the editor validates and
 * reviews it as any edit. Usage shows the tokens per question; End session
 * clears the chat.
 */

const ZERO = { calls: 0, input: 0, output: 0, thinking: 0, cached: 0, total: 0 };

/* A panel that opens and closes with the genie (index.css cq-genie-*): it
   stays shown for `ms` after `open` turns false, so its closing plays. */
export function useGenie(open, ms = 300) {
  const [shown, setShown] = useState(open);
  useEffect(() => {
    if (open) {
      setShown(true);
      return undefined;
    }
    const t = setTimeout(() => setShown(false), ms);
    return () => clearTimeout(t);
  }, [open, ms]);
  return { shown: open || shown, closing: !open && shown };
}

export function addUsage(a, b) {
  const out = { ...a };
  Object.keys(ZERO).forEach((k) => {
    out[k] = (a[k] || 0) + ((b && b[k]) || 0);
  });
  return out;
}

export function tokens(n) {
  if (n >= 10000) return `${Math.round(n / 1000)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

/* How many lines a proposal adds and removes (a plain line count, enough for the card). */
function lineDelta(before, after) {
  const left = new Map();
  before.split("\n").forEach((l) => left.set(l, (left.get(l) || 0) + 1));
  let added = 0;
  after.split("\n").forEach((l) => {
    if (left.get(l)) left.set(l, left.get(l) - 1);
    else added += 1;
  });
  const removed = [...left.values()].reduce((s, v) => s + v, 0);
  return { added, removed };
}

/* What the assistant may read, each by choice. It reads each only when a
   question needs it. With selected lines nothing else is shared. */
const PARTS = [
  ["script", "Script", "The script as it is in the editor, unsaved edits too."],
  ["packet", "Work packet", "The test's work packet from GitHub: what the test must do."],
  ["runs", "Runs", "The latest runs: their results and failed steps with errors."],
];
const PART_NAME = Object.fromEntries(PARTS.map(([id, name]) => [id, name]));
const READ_NAME = { lines: "Lines around", ...PART_NAME };
const NONE = { script: false, packet: false, runs: false };

/* Which parts can be picked: none without a packet or runs to give. */
function available(ctx) {
  return { script: true, packet: Boolean(ctx?.packet?.found), runs: Boolean(ctx?.runs?.length) };
}

/* About how many tokens the next question takes: who it is and the chat so
   far when it reads nothing (a "Hi"), and with everything allowed read (the
   script's rules come with the script). */
function questionTokens(ctx, use, scriptTokens, historyTokens) {
  const sizes = ctx?.sizes || {};
  const least = (sizes.base || 0) + historyTokens;
  const script = use.script ? (sizes.rules || 0) + scriptTokens : 0;
  return { least, most: least + script + PARTS.reduce((n, [id]) => n + (id !== "script" && use[id] ? sizes[id] || 0 : 0), 0) };
}

/* The chat bar's + Context box: one row per part the assistant may read.
   Selected lines in it go alone: the parts are off for that question. */
function ContextMenu({ ctx, use, onToggle, scriptTokens, total, onClose, closing = false, snippets = [], onRemove }) {
  const can = available(ctx);
  const sizes = ctx?.sizes || {};
  const ref = useRef(null);
  useEffect(() => {
    const away = (e) => ref.current && !ref.current.contains(e.target) && !e.target.closest?.("[data-context-toggle]") && onClose();
    const esc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [onClose]);
  const alone = snippets.length > 0;
  const rows = PARTS.map(([id, name, hint]) => ({
    id,
    name,
    hint: alone ? "Not shared: only the selected lines go with the next question." : can[id] ? hint : id === "packet" ? "No work packet was found for this test." : "This Artifact has no runs yet.",
    size: id === "script" ? scriptTokens : sizes[id] || 0,
    on: !alone && Boolean(use[id]),
    disabled: alone || !can[id],
  }));
  return (
    <div
      ref={ref}
      role="group"
      aria-label="What the assistant may read"
      className={`absolute bottom-full left-3 z-20 mb-1.5 w-72 overflow-hidden rounded-xl border border-slate-300 bg-card shadow-xl shadow-slate-900/15 dark:border-slate-600 ${closing ? "cq-genie-out-bl" : "cq-genie-in-bl"}`}
    >
      <p className="border-b border-slate-200 px-3 py-1.5 text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-400 dark:border-slate-700">The assistant may read</p>
      <ul className="py-0.5">
        {rows.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              role="switch"
              aria-checked={r.on}
              aria-label={r.name}
              disabled={r.disabled}
              onClick={() => onToggle(r.id)}
              className="flex w-full items-start gap-2.5 px-3 py-2 text-left transition enabled:hover:bg-brand-50/60 disabled:cursor-default"
            >
              <span
                className={`mt-px flex h-4 w-4 flex-none items-center justify-center rounded ${r.on ? "bg-brand-600 text-white" : "ring-1 ring-slate-300"} ${r.disabled ? "opacity-40" : ""}`}
                aria-hidden="true"
              >
                {r.on && <Icons.Check className="h-3 w-3" />}
              </span>
              <span className={`min-w-0 flex-1 ${r.disabled ? "opacity-50" : ""}`}>
                <span className="block text-[0.75rem] font-semibold leading-tight text-slate-800">{r.name}</span>
                <span className="mt-0.5 block text-[0.65625rem] leading-snug text-slate-500" title={r.hint}>
                  {r.hint}
                </span>
              </span>
              {!r.disabled && <span className="mt-px flex-none font-mono text-[0.65625rem] text-slate-400">~{tokens(r.size)}</span>}
            </button>
          </li>
        ))}
      </ul>
      {snippets.length > 0 && (
        <div className="border-t border-slate-200 dark:border-slate-700">
          <p className="px-3 pb-0.5 pt-1.5 text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-400">Selected lines · sent until you remove them</p>
          <ul className="cq-scroll max-h-40 overflow-y-auto pb-1" aria-label="Selected lines">
            {snippets.map((sn) => (
              <li key={`${sn.start}-${sn.end}`} className="group flex items-start gap-2 px-3 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800/60">
                <Icons.Script className="mt-0.5 h-3.5 w-3.5 flex-none text-amber-600" />
                <span className="min-w-0 flex-1">
                  <span className="block text-[0.75rem] font-semibold leading-tight text-slate-800">{linesLabel(sn)}</span>
                  <span className="mt-0.5 block truncate font-mono text-[0.65625rem] text-slate-500" title={sn.text}>
                    {(sn.text || "").split("\n").find((l) => l.trim()) || ""}
                  </span>
                </span>
                <span className="mt-px flex-none font-mono text-[0.65625rem] text-slate-400">~{tokens(Math.round((sn.text || "").length / 4))}</span>
                <button
                  type="button"
                  onClick={() => onRemove(sn)}
                  aria-label={`Remove ${linesLabel(sn).toLowerCase()}`}
                  title="Remove"
                  className="flex-none rounded p-0.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                >
                  <Icons.X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p
        className="flex items-center border-t border-slate-200 bg-slate-50/70 px-3 py-1.5 text-[0.65625rem] text-slate-500 dark:border-slate-700 dark:bg-slate-800/50"
        title="It reads each only when a question needs it, so a question about nothing here reads nothing. Kept until you change it."
      >
        <span className="flex-1">Read only when a question needs it</span>
        <span className="font-semibold text-slate-700">up to ≈ {tokens(total)}</span>
      </p>
    </div>
  );
}

/* The assistant needs a part that wasn't given: add it and ask again. */
function NeedsCard({ needs, active, onAdd }) {
  const names = needs.items.map((i) => PART_NAME[i] || i);
  return (
    <div className="mt-3 rounded-xl bg-amber-50/80 p-3 ring-1 ring-amber-200" aria-label="The assistant needs more context">
      <p className="flex items-center gap-1.5 text-[0.78125rem] font-semibold text-amber-900">
        <Icons.Alert className="h-4 w-4" /> Needs the {names.join(" and ").toLowerCase()}
      </p>
      <button
        type="button"
        onClick={onAdd}
        disabled={!active}
        className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-[0.75rem] font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
      >
        <Icons.Plus className="h-3.5 w-3.5" /> Add the {names.join(" and ").toLowerCase()} and ask again
      </button>
    </div>
  );
}

/* What the assistant said and did, as the next turn reads it. */
function asHistory(m) {
  let content = m.content;
  if (m.lines?.length) content += `\n[About the selected lines ${m.lines.map((x) => (x.start === x.end ? x.start : `${x.start}-${x.end}`)).join(", ")}]`;
  if (m.question) content += `\n[Asked: ${m.question.question} Options: ${m.question.options.join(" / ")}]`;
  if (m.proposal) content += `\n[Proposed a change: ${m.proposal.summary}${m.applied ? " (the user applied it to the editor)" : ""}]`;
  return { role: m.role, content };
}

/* The chat box grows with what is typed, up to about six lines. */
function grow(el) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
}

function Avatar() {
  return (
    <span
      className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-sm ring-2 ring-white dark:ring-slate-900"
      aria-hidden="true"
    >
      <Icons.Spark className="h-3 w-3" />
    </span>
  );
}

function UsagePanel({ usage, turns, model, onClose }) {
  const rows = [
    ["New input", Math.max(0, usage.input - usage.cached)],
    ["Cached input (reduced rate)", usage.cached],
    ["Output", usage.output],
    ["Thinking", usage.thinking],
  ];
  return (
    <div role="dialog" aria-label="Token usage" className="absolute right-3 top-14 z-20 w-80 rounded-xl border border-slate-200 bg-card p-4 shadow-xl">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex-1 text-[0.8125rem] font-semibold text-slate-800">Usage in this session</span>
        <button type="button" onClick={onClose} aria-label="Close the usage" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
          <Icons.X className="h-4 w-4" />
        </button>
      </div>
      <p className="text-[1.5rem] font-semibold leading-none text-slate-900">
        {usage.total.toLocaleString()} <span className="text-[0.75rem] font-medium text-slate-500">tokens</span>
      </p>
      <p className="mt-1 text-[0.71875rem] text-slate-500">
        {usage.calls} AI call{usage.calls === 1 ? "" : "s"} over {turns.length} question{turns.length === 1 ? "" : "s"}
        {model ? ` · ${model}` : ""}
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[0.75rem]">
        {rows.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt className="text-slate-500">{k}</dt>
            <dd className="text-right font-mono text-slate-800">{v.toLocaleString()}</dd>
          </React.Fragment>
        ))}
      </dl>
      {turns.length > 0 && (
        <table className="mt-3 w-full text-[0.71875rem]">
          <thead>
            <tr className="text-left text-slate-400">
              <th className="font-medium">Question</th>
              <th className="text-right font-medium">Calls</th>
              <th className="text-right font-medium">Tokens</th>
            </tr>
          </thead>
          <tbody>
            {turns.map((t, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="max-w-[9rem] truncate py-1 pr-2 text-slate-700" title={t.ask}>
                  {i + 1}. {t.ask}
                </td>
                <td className="py-1 text-right font-mono text-slate-500">{t.usage.calls}</td>
                <td className="py-1 text-right font-mono text-slate-800">{t.usage.total.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="mt-3 text-[0.6875rem] leading-snug text-slate-400">
        A question that reads nothing sends only a few lines. The script, work packet and runs are read only when a question needs them, and only those you allow (+ Context). The script's rules and the work packet, once read, are cached by the AI for 15 minutes and billed at a reduced rate, not sent again.
      </p>
    </div>
  );
}

/* Before anything is sent: what a session reads and costs. */
/* What waits for the session: "fix lines 7–8", "add lines 3, 7–8 to the chat". */
function waitingText(waiting) {
  const fixes = waiting.filter((w) => w.fix);
  const adds = waiting.filter((w) => !w.fix);
  const parts = [];
  if (fixes.length) parts.push(`fix ${linesLabel(fixes[fixes.length - 1]).toLowerCase()}`);
  if (adds.length) parts.push(`add ${linesLabel(addSnippetsOf(adds)).toLowerCase()} to the chat`);
  return parts.join(" and ");
}

/* `waiting`: Fix with AI / Add to chat clicked before the session started:
   said here, done once the user starts it. */
function StartScreen({ label, ended, onStart, waiting = [] }) {
  const facts = [
    [Icons.Book, "You choose what it reads", `${label}'s script, and its work packet and latest runs if you allow them, read only when a question needs them.`],
    [Icons.Chart, "Uses tokens", "Under 1k tokens for a question that reads nothing, more for what it reads; Usage shows them as you go."],
    [Icons.ShieldCheck, "Suggests only", "It proposes changes. You apply them, and Save still validates and shows them first."],
  ];
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto px-6 py-6">
      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-500/30">
        <Icons.Spark className="h-7 w-7" />
      </span>
      <h3 className="mt-4 text-[1rem] font-semibold tracking-tight text-slate-900">{ended ? "Session ended" : "Start an AI session"}</h3>
      <p className="mt-1 max-w-xs text-center text-[0.78125rem] leading-snug text-slate-500">
        {ended ? `The chat was cleared. ${ended}` : "Ask why a run failed, compare the script with its work packet, or get a fix proposed."}
      </p>
      <ul className="mt-5 w-full space-y-3">
        {facts.map(([Icon, title, text]) => (
          <li key={title} className="flex gap-3">
            <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-white/80 text-brand-700 shadow-sm ring-1 ring-brand-100 dark:bg-slate-800">
              <Icon className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-[0.78125rem] font-semibold text-slate-800">{title}</span>
              <span className="block text-[0.75rem] leading-snug text-slate-500">{text}</span>
            </span>
          </li>
        ))}
      </ul>
      {waiting.length > 0 && (
        <p role="status" className="mt-5 flex w-full items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-[0.78125rem] leading-snug text-amber-900 ring-1 ring-amber-200">
          <Icons.Alert className="mt-0.5 h-4 w-4 flex-none text-amber-600" />
          <span>
            The AI session is off. Start it to {waitingText(waiting)}.
          </span>
        </p>
      )}
      <button
        type="button"
        onClick={onStart}
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-brand-500/25 transition hover:brightness-110"
      >
        <Icons.Spark className="h-4 w-4" /> {ended ? "Start a new session" : "Start AI session"}
      </button>
      <p className="mt-2 text-[0.6875rem] text-slate-400">Nothing is sent to the AI until you ask a question.</p>
    </div>
  );
}

/* A question with its answers: one click answers it; Other… types one. */
function QuestionCard({ question, answered, active, onPick, onOther }) {
  return (
    <div className="mt-3" role="group" aria-label="Choose an answer">
      <p className="text-[0.8125rem] font-semibold text-slate-900">{question.question}</p>
      <div className="mt-2 space-y-1.5">
        {question.options.map((o, k) => {
          const chosen = answered === o;
          return (
            <button
              key={o}
              type="button"
              onClick={() => onPick(o)}
              disabled={!active}
              className={[
                "group flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[0.78125rem] font-medium transition",
                chosen
                  ? "bg-brand-600 text-white shadow-sm"
                  : "bg-white/80 text-slate-800 shadow-sm ring-1 ring-slate-200/80 enabled:hover:ring-brand-400 enabled:hover:bg-white dark:bg-slate-800",
                !active && !chosen ? "opacity-45" : "",
              ].join(" ")}
            >
              <span className={`flex h-5 w-5 flex-none items-center justify-center rounded-full text-[0.65625rem] font-bold ${chosen ? "bg-white/25" : "bg-brand-50 text-brand-700"}`}>
                {k + 1}
              </span>
              <span className="min-w-0 flex-1">{o}</span>
              {chosen ? <Icons.Check className="h-4 w-4 flex-none" /> : <Icons.ChevronRight className="h-4 w-4 flex-none text-slate-300 group-enabled:group-hover:text-brand-500" />}
            </button>
          );
        })}
        <button
          type="button"
          onClick={onOther}
          disabled={!active}
          className={`flex w-full items-center gap-2.5 rounded-xl border border-dashed border-slate-300 px-3 py-2 text-left text-[0.78125rem] font-medium text-slate-500 transition enabled:hover:border-brand-400 enabled:hover:text-slate-800 ${!active ? "opacity-45" : ""}`}
        >
          <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800">
            <Icons.Pencil className="h-3 w-3" />
          </span>
          {answered && !question.options.includes(answered) ? `Other: ${answered}` : "Other…"}
        </button>
      </div>
    </div>
  );
}

function ProposalCard({ proposal, code, readOnly, applied, onView, onApply, onOther }) {
  const delta = useMemo(() => lineDelta(code, proposal.script), [code, proposal.script]);
  return (
    <div className="mt-2 overflow-hidden rounded-xl border border-brand-200 bg-card shadow-sm" aria-label="Proposed change">
      <div className="flex items-center gap-2 border-b border-brand-100 bg-brand-50/60 px-3 py-2">
        <Icons.Pencil className="h-3.5 w-3.5 text-brand-700" />
        <span className="flex-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-brand-700">Proposed change</span>
        {applied ? (
          <span className="inline-flex items-center gap-1 text-[0.71875rem] font-semibold text-emerald-700">
            <Icons.Check className="h-3.5 w-3.5" /> Applied
          </span>
        ) : (
          <span className="text-[0.71875rem] font-semibold">
            <span className="text-emerald-700">+{delta.added}</span> <span className="text-rose-700">−{delta.removed}</span>
          </span>
        )}
      </div>
      <div className="p-3">
        {proposal.summary && <p className="text-[0.8125rem] font-medium text-slate-800">{proposal.summary}</p>}
        {proposal.changes?.length > 0 && (
          <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-[0.78125rem] text-slate-600">
            {proposal.changes.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        )}
        <p className="mt-2 text-[0.71875rem] text-slate-500">
          {applied
            ? "In the editor now, not saved. Save validates it and shows the changes first."
            : `Validated: ${proposal.steps} steps${proposal.warnings?.length ? `, ${proposal.warnings.length} warning${proposal.warnings.length === 1 ? "" : "s"}` : ", no problems"}.`}
        </p>
        {!applied && <p className="mt-3 text-[0.78125rem] font-semibold text-slate-800">Apply these changes to the editor?</p>}
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={onView}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[0.75rem] font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
          >
            <Icons.Layers className="h-3.5 w-3.5 text-brand-600" /> View changes
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={readOnly || applied}
            title={readOnly ? "The script is read-only now" : "Put this script in the editor; Save still asks first"}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-2.5 py-1.5 text-[0.75rem] font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            <Icons.Check className="h-3.5 w-3.5" /> {applied ? "Applied" : "Apply to editor"}
          </button>
        </div>
        {!applied && onOther && (
          <button
            type="button"
            onClick={onOther}
            className="mt-1.5 flex w-full items-center gap-2 rounded-lg border border-dashed border-slate-300 px-2.5 py-1.5 text-left text-[0.75rem] font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-800"
          >
            <Icons.Pencil className="h-3 w-3" /> Something else… ask for a different change
          </button>
        )}
      </div>
    </div>
  );
}

function Thinking({ since }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="flex items-center gap-2.5" aria-label="The assistant is thinking">
      <Avatar />
      <span className="flex gap-1" aria-hidden="true">
        {[0, 150, 300].map((d) => (
          <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-500" style={{ animationDelay: `${d}ms` }} />
        ))}
      </span>
      <span className="text-[0.75rem] text-slate-500">Thinking… {Math.max(0, Math.round((now - since) / 1000))}s</span>
    </div>
  );
}

/* "Lines 4–7" (or "Line 4"). */
export function linesLabel(sel) {
  if (Array.isArray(sel)) {
    const parts = sel.map((x) => (x.start === x.end ? `${x.start}` : `${x.start}–${x.end}`));
    return sel.length === 1 ? linesLabel(sel[0]) : `Lines ${parts.join(", ")}`;
  }
  return sel.start === sel.end ? `Line ${sel.start}` : `Lines ${sel.start}–${sel.end}`;
}

/* A snippet where its lines are now: the script may have changed since it
   was added (lines moved: found again by its text; changed: null). */
export function relocate(code, sn) {
  const ls = code.split("\n");
  if (ls.slice(sn.start - 1, sn.end).join("\n") === sn.text) return sn;
  const n = sn.end - sn.start + 1;
  for (let i = 0; i + n <= ls.length; i++) {
    if (ls.slice(i, i + n).join("\n") === sn.text) return { ...sn, start: i + 1, end: i + n };
  }
  return null;
}

/* Add a snippet: one that overlaps or touches others joins them into one
   (11–16, then 12–16 and 13–16, stay "Lines 11–16"), its text read again from
   the script; the rest stay apart, in script order. Snippets whose lines
   changed since they were added are taken as they are now. */
/* The adds waiting for the session, joined as the box would join them. */
function addSnippetsOf(adds) {
  return adds.reduce((xs, a) => {
    const i = xs.findIndex((x) => x.start <= a.end + 1 && x.end >= a.start - 1);
    if (i < 0) return [...xs, { start: a.start, end: a.end }].sort((p, q) => p.start - q.start);
    const j = { start: Math.min(xs[i].start, a.start), end: Math.max(xs[i].end, a.end) };
    return addSnippetsOf([...xs.filter((_, k) => k !== i), j]);
  }, []);
}

export function addSnippet(snippets, sn, code) {
  const ls = code.split("\n");
  let { start, end } = sn;
  const apart = [];
  for (const x of snippets.map((y) => relocate(code, y) || y)) {
    if (x.start <= end + 1 && x.end >= start - 1) {
      start = Math.min(start, x.start);
      end = Math.max(end, x.end);
    } else apart.push(x);
  }
  /* A join can reach one that was apart before it grew. */
  const joined = { start, end, text: ls.slice(start - 1, end).join("\n") };
  const again = apart.some((x) => x.start <= end + 1 && x.end >= start - 1);
  return again ? addSnippet(apart, joined, code) : [...apart, joined].sort((a, b) => a.start - b.start);
}

/* `request`: lines selected in the editor ({start, end, text, fix, id}, a
   new id each time). Add to chat puts them in the context box (+ Context),
   where each can be removed; they go with the next question instead of the
   script. Fix with AI asks about those lines at once. */
export default function ArtifactAiPanel({ testcasekey, label, code, readOnly, onApply, onViewProposal, onHide, request = null }) {
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState("");
  const [ctx, setCtx] = useState(null);
  const [ctxErr, setCtxErr] = useState("");
  const [messages, setMessages] = useState([]); // {role, content, question?, proposal?, answered?, applied?, usage?, secs?}
  const [draft, setDraft] = useState("");
  const [busySince, setBusySince] = useState(0);
  const [err, setErr] = useState("");
  const [usage, setUsage] = useState(ZERO);
  const [turns, setTurns] = useState([]); // {ask, usage}
  const [model, setModel] = useState("");
  const [showUsage, setShowUsage] = useState(false);
  const [answering, setAnswering] = useState(false); // Other… : the box answers the open question
  /* What the assistant may read for the next question; kept until changed. */
  const [use, setUse] = useState({ script: true, packet: false, runs: false });
  const [contextOpen, setContextOpen] = useState(false);
  const contextBox = useGenie(contextOpen, 240);
  /* Lines selected in the editor ({start, end, text, sent?}): Add to chat and
     Fix with AI both put them here. They go with each question, alone, until
     the user removes them (×), so a follow-up stays about the same lines. */
  const [snippets, setSnippets] = useState([]);
  const fixRef = useRef(null);
  const abortRef = useRef(null);
  const endRef = useRef(null);
  const boxRef = useRef(null);
  const [ask, confirmDialog] = useConfirm();
  const busy = busySince > 0;

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    if (!started) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}testcases/${encodeURIComponent(testcasekey)}/script/assistant`);
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(typeof body?.detail === "string" ? body.detail : `HTTP ${res.status}`);
        if (alive) {
          setCtx(body.data);
          if (body.data.model) setModel(body.data.model);
        }
      } catch (e) {
        if (alive) setCtxErr(e.message || "Could not read what the assistant knows.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [started, testcasekey]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: "end" });
  }, [messages, busy]);

  /* What the editor's buttons asked for before the session was started:
     nothing is read or sent until the user starts it. */
  const [waiting, setWaiting] = useState([]);
  const take = (req) => {
    const sn = { start: req.start, end: req.end, text: req.text ?? "" };
    setSnippets((xs) => addSnippet(xs, sn, code));
    if (req.fix) {
      // In the context box like Add to chat's, and asked about at once.
      fixRef.current = sn;
      return;
    }
    setContextOpen(true); // shows where it went, and how to take it off
    requestAnimationFrame(() => boxRef.current?.focus());
  };
  const requestId = request?.id;
  useEffect(() => {
    if (!requestId) return;
    if (started) take(request);
    else setWaiting((ws) => [...ws, request]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);
  const startSession = () => {
    setStarted(true);
    setEnded("");
    waiting.forEach(take);
    setWaiting([]);
  };

  /* The question still open: the last message's, while unanswered. */
  const last = messages[messages.length - 1];
  const openQuestion = last?.role === "assistant" && last.question?.options?.length && !last.answered ? messages.length - 1 : -1;

  const send = async (text, { parts, sels = snippets } = {}) => {
    const said = (text || "").trim();
    if (!said || busy) return;
    /* The snippets where their lines are now. One already sent whose lines
       changed (a fix applied) is taken as those lines read now; one never sent
       is left out, and said so. */
    const lines = code.split("\n");
    const found = sels.map((sn) => relocate(code, sn) || (sn.sent && sn.end <= lines.length
      ? { ...sn, text: lines.slice(sn.start - 1, sn.end).join("\n") } : null));
    const sel = found.filter(Boolean);
    const lost = sels.filter((_, i) => !found[i]);
    const can = available(ctx);
    const picked = parts ? Object.fromEntries(PARTS.map(([id]) => [id, parts.includes(id) && can[id]])) : use;
    if (parts) setUse(picked);
    /* Selected lines (Fix with AI, Add to chat) are shared alone: no script, packet or runs. */
    const allowed = sel.length ? NONE : picked;
    const sentWith = PARTS.filter(([id]) => allowed[id]).map(([id]) => id);
    const history = [
      ...messages.map((m, i) => (i === openQuestion ? { ...m, answered: said } : m)),
      { role: "user", content: said, sentWith, lines: sel.length ? sel : undefined },
    ];
    setMessages(history);
    // Kept in the box for the next question, where their lines are now.
    setSnippets((xs) => xs.flatMap((x) => {
      const i = sels.indexOf(x);
      if (i < 0) return [x];
      return found[i] ? [{ ...found[i], sent: true }] : [];
    }));
    setDraft("");
    setErr(lost.length ? `${linesLabel(lost)} changed after you added ${lost.length === 1 ? "it" : "them"}, so ${lost.length === 1 ? "it was" : "they were"} left out.` : "");
    setAnswering(false);
    const since = Date.now();
    setBusySince(since);
    const ctl = new AbortController();
    abortRef.current = ctl;
    try {
      const res = await fetch(`${API_BASE_URL}testcases/${encodeURIComponent(testcasekey)}/script/assistant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: ctl.signal,
        body: JSON.stringify({
          script: code,
          // The newest question goes as typed: its lines are sent as `selections`.
          messages: history.map((m, i) => (i === history.length - 1 ? { role: m.role, content: m.content } : asHistory(m))),
          context: { script: Boolean(allowed.script), packet: Boolean(allowed.packet), runs: Boolean(allowed.runs) },
          ...(sel.length ? { selections: sel.map((x) => ({ start: x.start, end: x.end })) } : {}),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body?.detail === "string" ? body.detail : `HTTP ${res.status}`);
      const d = body.data;
      const turn = { ...ZERO, ...d.usage };
      setMessages((ms) => [
        ...ms,
        { role: "assistant", content: d.reply || "", question: d.question, proposal: d.proposal, needs: d.needs, read: d.read, usage: turn, secs: Math.round((Date.now() - since) / 1000) },
      ]);
      setUsage((u) => addUsage(u, turn));
      setTurns((t) => [...t, { ask: said, usage: turn }]);
      if (d.model) setModel(d.model);
    } catch (e) {
      if (e.name === "AbortError") setErr("Stopped. Ask again when ready.");
      else setErr(e.message || "The assistant did not answer.");
    } finally {
      setBusySince(0);
      abortRef.current = null;
    }
  };

  useEffect(() => {
    if (!ctx || busy || !fixRef.current) return;
    const sn = fixRef.current;
    // Asked once its lines are in the box (the state follows a render later).
    if (!snippets.some((x) => x.start <= sn.start && x.end >= sn.end)) return;
    fixRef.current = null;
    // With every snippet in the box: the fixed lines are among them.
    send(`Fix ${linesLabel(sn).toLowerCase()}.`, { sels: snippets });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, busy, requestId, snippets]);

  const apply = async (i) => {
    const m = messages[i];
    if (!m?.proposal) return;
    if (!(await onApply(m.proposal.script))) return;
    setMessages((ms) => ms.map((x, k) => (k === i ? { ...x, applied: true } : x)));
  };

  const scriptTokens = Math.round(code.length / 4);
  const perQuestion = questionTokens(ctx, use, scriptTokens, Math.round(messages.reduce((n, m) => n + asHistory(m).content.length, 0) / 4));

  /* Add what the assistant asked for, and ask the question again. */
  const addAndAsk = (i) => {
    const m = messages[i];
    const question = [...messages.slice(0, i)].reverse().find((x) => x.role === "user");
    if (!m?.needs || !question) return;
    const parts = [...new Set([...PARTS.filter(([id]) => use[id]).map(([id]) => id), ...m.needs.items])];
    send(question.content, { parts });
  };

  const other = () => {
    setAnswering(true);
    requestAnimationFrame(() => boxRef.current?.focus());
  };

  const endSession = async () => {
    if (messages.length) {
      const ok = await ask({
        title: "End the AI session",
        message: `End this AI session? The conversation is cleared (${usage.total.toLocaleString()} tokens used). Changes you applied stay in the editor.`,
        confirmLabel: "End session",
      });
      if (!ok) return;
    }
    abortRef.current?.abort();
    setEnded(turns.length ? `It used ${usage.total.toLocaleString()} tokens over ${turns.length} question${turns.length === 1 ? "" : "s"}.` : " ");
    setStarted(false);
    setCtx(null);
    setCtxErr("");
    setMessages([]);
    setSnippets([]);
    setUsage(ZERO);
    setTurns([]);
    setErr("");
    setDraft("");
    setShowUsage(false);
    setAnswering(false);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(draft);
    }
  };

  useEffect(() => grow(boxRef.current), [draft]);

  return (
    <aside
      aria-label="AI assistant"
      className="relative flex h-full min-h-0 flex-col bg-gradient-to-b from-brand-50/80 via-card to-card dark:from-brand-500/10"
    >
      <div className="flex h-12 flex-none items-center gap-2.5 border-b border-brand-100/70 px-4 dark:border-slate-700">
        <span className="flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-sm">
          <Icons.Spark className="h-3.5 w-3.5" />
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-2" title={started && model ? `Session active · ${model}` : undefined}>
          <span className="flex-none whitespace-nowrap text-[0.8125rem] font-semibold text-slate-900">AI assistant</span>
          <span className={`h-1.5 w-1.5 flex-none rounded-full ${started ? "bg-emerald-500" : "bg-slate-300"}`} />
          <span className="truncate whitespace-nowrap text-[0.71875rem] text-slate-500">{started ? "Active" : "No session"}</span>
        </span>
        {started && (
          <>
            <button
              type="button"
              onClick={() => setShowUsage((s) => !s)}
              aria-expanded={showUsage}
              title="Tokens this session has used"
              className="inline-flex flex-none items-center gap-1 rounded-full bg-white/80 px-2 py-0.5 text-[0.71875rem] font-semibold text-slate-600 shadow-sm ring-1 ring-slate-200/80 hover:text-slate-900 dark:bg-slate-800"
            >
              <Icons.Chart className="h-3.5 w-3.5" /> Usage · {tokens(usage.total)}
            </button>
            <button
              type="button"
              onClick={endSession}
              aria-label="End session"
              title="End the session and clear the chat"
              className="flex-none rounded-md p-1 text-slate-400 transition hover:bg-white hover:text-rose-600"
            >
              <Icons.LogOut className="h-4 w-4" />
            </button>
          </>
        )}
        <button type="button" onClick={onHide} title="Minimise the assistant (the chat is kept)" aria-label="Hide the assistant" className="flex-none rounded-md p-1 text-slate-400 transition hover:bg-white hover:text-slate-700">
          <Icons.Minus className="h-4 w-4" />
        </button>
      </div>
      {showUsage && <UsagePanel usage={usage} turns={turns} model={model} onClose={() => setShowUsage(false)} />}

      {!started ? (
        <StartScreen
          label={label}
          ended={ended}
          waiting={waiting}
          onStart={startSession}
        />
      ) : (
        <>
          <div className="cq-scroll min-h-0 flex-1 space-y-5 overflow-y-auto px-4 pb-4 pt-3" aria-live="polite">
            {ctxErr ? (
              <p className="text-[0.78125rem] text-rose-700">{ctxErr}</p>
            ) : !ctx ? (
              <p className="flex items-center gap-2 text-[0.78125rem] text-slate-500">
                <Icons.Spinner className="h-4 w-4 animate-spin" /> Reading the work packet and the runs…
              </p>
            ) : (
              <div className="flex gap-3">
                <Avatar />
                <div className="min-w-0 flex-1">
                  <p className="text-[0.84375rem] leading-relaxed text-slate-800">{ctx.intro}</p>
                  {messages.length === 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5" aria-label="Starters">
                      {ctx.starters.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => send(s.prompt, { parts: s.context || [] })}
                          className={[
                            "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[0.75rem] font-semibold shadow-sm transition",
                            s.id === "suggest"
                              ? "bg-gradient-to-r from-brand-600 to-brand-700 text-white hover:brightness-110"
                              : "bg-white/80 text-slate-700 ring-1 ring-slate-200/80 hover:text-brand-700 hover:ring-brand-300 dark:bg-slate-800",
                          ].join(" ")}
                        >
                          {s.id === "suggest" && <Icons.Spark className="h-3.5 w-3.5" />}
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {messages.map((m, i) =>
              m.role === "user" ? (
                <div key={i} className="flex flex-col items-end pl-10">
                  {/* The selected lines it was sent with, as attachments: all it shares. */}
                  {m.lines?.map((sn) => (
                    <figure key={`${sn.start}-${sn.end}`} aria-label={`Sent with ${linesLabel(sn).toLowerCase()}`} className="mb-1 w-full max-w-full overflow-hidden rounded-xl border border-amber-200 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-900/20">
                      <figcaption className="flex items-center gap-1.5 border-b border-amber-200 px-2.5 py-1 text-[0.65625rem] font-semibold text-amber-800 dark:border-amber-800">
                        <Icons.Script className="h-3 w-3" /> {linesLabel(sn)}
                      </figcaption>
                      <pre className="cq-scroll max-h-32 overflow-auto px-2.5 py-1.5 font-mono text-[0.6875rem] leading-snug text-slate-700">
                        {(sn.text || "").split("\n").map((l, k) => (
                          <div key={k}>
                            <span className="mr-2 inline-block w-6 select-none text-right text-slate-400">{sn.start + k}</span>
                            {l}
                          </div>
                        ))}
                      </pre>
                    </figure>
                  ))}
                  <div className="whitespace-pre-wrap rounded-2xl rounded-br-md bg-slate-900 px-3.5 py-2 text-[0.8125rem] leading-relaxed text-white shadow-sm dark:bg-brand-600">
                    {m.content}
                  </div>
                  {m.sentWith && (
                    <span className="mt-1 text-[0.65625rem] text-slate-400">
                      {m.lines ? `${linesLabel(m.lines)} only` : `May read: ${m.sentWith.length ? m.sentWith.map((id) => PART_NAME[id]).join(" · ") : "nothing"}`}
                    </span>
                  )}
                </div>
              ) : (
                <div key={i} className="flex gap-3">
                  <Avatar />
                  <div className="min-w-0 flex-1">
                    {m.content && (
                      <div className="text-[0.84375rem] leading-relaxed text-slate-800 [&_code]:break-all [&_code]:rounded [&_code]:bg-white/80 [&_code]:px-1 [&_code]:text-[0.75rem] [&_code]:text-brand-800 [&_strong]:text-slate-900">
                        <ChatMarkdown text={m.content} />
                      </div>
                    )}
                    {m.proposal && (
                      <ProposalCard
                        proposal={m.proposal}
                        code={code}
                        readOnly={readOnly}
                        applied={m.applied}
                        onView={() => onViewProposal(m.proposal.script)}
                        onApply={() => apply(i)}
                        onOther={i === messages.length - 1 ? () => requestAnimationFrame(() => boxRef.current?.focus()) : null}
                      />
                    )}
                    {m.needs?.items?.length > 0 && <NeedsCard needs={m.needs} active={i === messages.length - 1 && !busy} onAdd={() => addAndAsk(i)} />}
                    {m.question?.options?.length > 0 && (
                      <QuestionCard question={m.question} answered={m.answered} active={i === openQuestion && !busy} onPick={(o) => send(o)} onOther={other} />
                    )}
                    {m.usage && (
                      <p className="mt-2 text-[0.65625rem] text-slate-400">
                        {tokens(m.usage.total)} tokens{m.secs != null ? ` · ${m.secs}s` : ""}
                        {m.read && ` · ${m.read.length ? `read the ${m.read.map((id) => (READ_NAME[id] || id).toLowerCase()).join(", ")}` : "read nothing"}`}
                      </p>
                    )}
                  </div>
                </div>
              ),
            )}
            {busy && <Thinking since={busySince} />}
            {err && <p className="text-[0.78125rem] text-rose-700">{err}</p>}
            <div ref={endRef} />
          </div>

          {/* The chat bar: what goes with the question, then a floating box that grows as you type. */}
          <div className="relative flex-none px-3 pb-3 pt-1">
            {ctx && contextBox.shown && (
              <ContextMenu
                closing={contextBox.closing}
                ctx={ctx}
                use={use}
                onToggle={(id) => setUse((u) => ({ ...u, [id]: !u[id] }))}
                scriptTokens={scriptTokens}
                total={perQuestion.most + Math.round(snippets.reduce((n, x) => n + x.text.length, 0) / 4)}
                onClose={() => setContextOpen(false)}
                snippets={snippets}
                onRemove={(sn) => setSnippets((xs) => xs.filter((x) => x !== sn))}
              />
            )}
            {answering && openQuestion >= 0 && (
              <p className="mb-1.5 flex items-center gap-1.5 px-1 text-[0.71875rem] font-medium text-brand-700">
                <Icons.Pencil className="h-3 w-3 flex-none" />
                <span className="truncate">Your own answer to: {messages[openQuestion].question.question}</span>
                <button type="button" onClick={() => setAnswering(false)} className="ml-auto flex-none text-slate-400 hover:text-slate-700" aria-label="Back to the options">
                  <Icons.X className="h-3.5 w-3.5" />
                </button>
              </p>
            )}
            <div className="rounded-2xl border border-slate-300 bg-white shadow-lg shadow-brand-900/10 transition focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-100 dark:border-slate-600 dark:bg-slate-900">
              <textarea
                ref={boxRef}
                rows={1}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKey}
                disabled={!ctx}
                aria-label={`Ask the assistant about ${label}`}
                placeholder={answering ? "Type your answer…" : openQuestion >= 0 ? "Pick an answer above, or type your own…" : "Ask about a step, or what to change…"}
                className="block max-h-[9.375rem] min-h-[2.75rem] w-full resize-none bg-transparent px-4 pb-1 pt-3 text-[0.84375rem] text-slate-800 outline-none placeholder:text-slate-400"
              />
              <div className="flex items-center gap-1.5 px-2.5 pb-2.5">
                <button
                  type="button"
                  data-context-toggle
                  onClick={() => setContextOpen((o) => !o)}
                  disabled={!ctx}
                  aria-expanded={contextOpen}
                  aria-label="Choose what the assistant may read"
                  title="Choose what the assistant may read: the script, the work packet and the runs. It reads each only when a question needs it. Selected lines go alone."
                  className={`inline-flex h-7 flex-none items-center gap-1 rounded-full px-2.5 text-[0.71875rem] font-semibold transition disabled:opacity-40 ${contextOpen ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-brand-50 hover:text-brand-700 dark:bg-slate-800"}`}
                >
                  <Icons.Plus className="h-3.5 w-3.5" /> Context
                  {snippets.length > 0 && (
                    <span
                      className={`ml-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[0.625rem] font-bold ${contextOpen ? "bg-card text-brand-700" : "bg-amber-500 text-white"}`}
                      title={`${snippets.length} selected snippet${snippets.length === 1 ? "" : "s"} go with the next question`}
                    >
                      {snippets.length}
                    </span>
                  )}
                </button>
                {/* What goes with the question, as attachments. */}
                <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden" aria-label="The assistant may read">
                  {snippets.length > 0 ? (
                    <span className="inline-flex flex-none items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[0.6875rem] font-medium text-amber-800" title="With selected lines, nothing else is shared: no script, work packet or runs">
                      <Icons.Script className="h-3 w-3" /> Only the selected lines
                    </span>
                  ) : PARTS.filter(([id]) => use[id]).map(([id, name]) => (
                    <span key={id} className="inline-flex flex-none items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[0.6875rem] font-medium text-brand-700">
                      {name}
                      <button type="button" onClick={() => setUse((u) => ({ ...u, [id]: false }))} aria-label={`Don't allow the ${name.toLowerCase()}`} className="text-brand-400 hover:text-brand-800">
                        <Icons.X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </span>
                <span
                  className="ml-auto flex-none whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-[0.65625rem] font-medium text-slate-500 dark:bg-slate-800"
                  title={`About ${perQuestion.least.toLocaleString()} tokens for a question that reads nothing, up to ${perQuestion.most.toLocaleString()} if it reads all you allow · ${usage.total.toLocaleString()} used this session${model ? ` · ${model}` : ""}`}
                >
                  ≈ {tokens(perQuestion.least)}+
                </span>
                {busy ? (
                  <button
                    type="button"
                    onClick={() => abortRef.current?.abort()}
                    aria-label="Stop"
                    title="Stop"
                    className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-slate-900 text-white shadow-sm transition hover:bg-slate-700"
                  >
                    <span className="h-2.5 w-2.5 rounded-sm bg-white" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => send(draft)}
                    disabled={!draft.trim() || !ctx}
                    aria-label="Send"
                    className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-md shadow-brand-500/30 transition hover:brightness-110 disabled:from-slate-300 disabled:to-slate-300 disabled:shadow-none"
                  >
                    <Icons.ArrowLeft className="h-4 w-4 rotate-90" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {confirmDialog}
    </aside>
  );
}
